import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { useCollaborators } from '../../../../../hooks/api/useCollaborators';
import { useLoadedUser } from '../../../../../hooks/api/useUser';
import getName from '../../../../../utils/getName';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import InviteForm, { getFeatureToggledDefaultOptions } from './InviteForm';
import InviteCareTeamMemberHeader from './InviteCareTeamMemberHeader';
import { useConfirmBackNavigationOnDirtyForm } from '../../../../shared/FormExitValidation';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { getIntl } from '../../../../../utils/intl';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';
import { withRemountOnFocus } from '../../../../HOC/withRemountOnFocus';
import useEvaluatedPermissions from '../../../../../hooks/api/useEvaluatedPermissions';

const intl = getIntl('components.screens.tabs.profiles.inviteCollaboratorScreen', {
    invite: 'Invite',
});

export type InviteCollaboratorScreeOwnProps = {};

export type InviteCollaboratorScreeProps = InviteCollaboratorScreeOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'invite-collaborator'>;
};

const InviteCollaborator: React.FC<InviteCollaboratorScreeProps> = ({
    route: {
        params: { profileId, careTeamMemberId, defaultEmail, defaultFirstName, defaultLastName },
    },
}) => {
    const { permissions } = useEvaluatedPermissions({ medicalProfileId: profileId });
    const isMobile = useIsMobileLayout();
    const goBack = useGoBack();
    const { user } = useLoadedUser({});
    const adminName = getName({
        firstName: user?.firstName,
        lastName: user?.lastName,
    });
    const { profile } = useMedicalProfile({ medicalProfileId: profileId });
    const profileName = getName({
        firstName: profile?.contactDetails?.firstName,
        lastName: profile?.contactDetails?.lastName,
    });
    const navigation = useProfilesTabNavigation();
    useConfirmBackNavigationOnDirtyForm(navigation);
    const { inviteCollaborator, isInvitingCollaborator } = useCollaborators({
        medicalProfileId: profileId,
        careTeamMemberId,
        onInviteSuccess: () => {
            if (isMobile) {
                navigation.navigate('profile', { profileId });
            } else {
                navigation.navigate('profile-activity', { profileId });
            }
        },
    });

    const initialValues = React.useMemo(
        () => ({
            ...getFeatureToggledDefaultOptions(),
            email: defaultEmail || '',
            firstName: defaultFirstName || '',
            lastName: defaultLastName || '',
        }),
        [defaultEmail, defaultFirstName, defaultLastName],
    );

    const form = (
        <InviteForm
            onCancel={goBack}
            adminName={adminName}
            profileName={profileName}
            isSubmitting={isInvitingCollaborator}
            initialValues={initialValues}
            saveText={intl('invite')}
            onSubmit={(values) => {
                const { acceptConsent: _acceptConsent, ...toSend } = values;
                inviteCollaborator({ ...toSend, careTeamMemberId });
            }}
            mostPermissiveAppPermissions={permissions.permissions}
        />
    );

    return (
        <>
            {careTeamMemberId ? (
                <>
                    <InviteCareTeamMemberHeader careTeamMemberId={careTeamMemberId} profileId={profileId} />
                    {form}
                </>
            ) : (
                <ProfileHeaderContainer profileId={profileId} bottomBorder omitChatButton>
                    {form}
                </ProfileHeaderContainer>
            )}
        </>
    );
};

export default React.memo(withRemountOnFocus(InviteCollaborator));
